import { I18n } from 'react-redux-i18n';
import {
  AccountActivityElementModel,
  AccountActivityAccountModel,
  AccountActivityOutcome,
} from './types';
import {
  activityTypeVerbs,
  activitySubtypeVerbs,
  targetEntityConfig,
  outcomeConfig,
  OutcomeConfig,
  i18nKeys,
} from './constants';

// ═══════════════════════════════════════════════════════════════════════════
// Actor Formatting
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Format the account name for display
 */
export const formatAccountName = (account: AccountActivityAccountModel | null): string => {
  if (!account) return 'Unknown';
  
  const { firstName, lastName, email } = account;
  
  if (firstName && lastName) {
    return `${firstName} ${lastName}`;
  }
  
  return email || 'Unknown';
};

/**
 * Format the actor display with optional impersonation info
 * Returns { displayName (email), fullName, isImpersonated, impersonatorName }
 */
export const formatActor = (activity: AccountActivityElementModel): {
  displayName: string;
  fullName: string | null;
  accountId: number | null;
  isAccessKey: boolean;
  isImpersonated: boolean;
  impersonatorName: string | null;
  impersonatorEmail: string | null;
} => {
  const account = activity.account;

  // An integration key acting on its own has no account at all. Naming the key is the difference
  // between "we know a machine did this and which one" and "Unknown", which is what the fallback
  // below would otherwise have rendered for every key-driven request.
  const isAccessKey = !activity.accountId && !!activity.accessKeyId;
  const keyName = activity.accessKey?.description
    ? `API key: ${activity.accessKey.description}`
    : 'API key';

  const displayName = account?.email || (isAccessKey ? keyName : 'Unknown');
  const fullName = account?.firstName && account?.lastName
    ? `${account.firstName} ${account.lastName}`
    : null;
  const isImpersonated = activity.impersonatorAccountId !== null;
  const impersonatorName = isImpersonated
    ? formatAccountName(activity.impersonatorAccount)
    : null;
  const impersonatorEmail = isImpersonated
    ? activity.impersonatorAccount?.email || null
    : null;

  return {
    displayName,
    fullName,
    accountId: activity.accountId,
    isAccessKey,
    isImpersonated,
    impersonatorName,
    impersonatorEmail,
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// Action Formatting
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get the action verb based on activity type and subtype
 */
export const formatAction = (activity: AccountActivityElementModel): string => {
  const { activityType, activitySubtype, requestPath } = activity;

  // Request-path overrides
  if (activityType === 'read' && requestPath) {
    if (/^\/api\/v\d+\/dataexport\/download\/\d+$/i.test(requestPath)) {
      return 'downloaded';
    }
    if (/^\/api\/v\d+\/(page\/(campaign|event)|organization)\/\d+\/emailJourneyCustomEntries$/i.test(requestPath)) {
      return 'viewed backoffice configuration for';
    }
  }

  // Check if subtype has a specific verb override
  if (activitySubtype && activitySubtypeVerbs[activitySubtype]) {
    return activitySubtypeVerbs[activitySubtype]!;
  }

  // Fall back to activity type verb
  return activityTypeVerbs[activityType] || activityType;
};

// ═══════════════════════════════════════════════════════════════════════════
// Target Formatting
// ═══════════════════════════════════════════════════════════════════════════

interface TargetInfo {
  description: string;
  link: string | null;
}

/**
 * Get target entity name based on the target type
 */
const getTargetName = (activity: AccountActivityElementModel): string | undefined => {
  const { targetEntity } = activity;

  if (!targetEntity) return undefined;

  switch (targetEntity) {
    case 'account':
      return formatAccountName(activity.targetAccount);
    case 'organization':
      return activity.targetOrganization?.name;
    case 'accessKey':
      return activity.targetAccessKey?.description;
    case 'campaign':
      return activity.targetCampaign?.name;
    case 'event':
      return activity.targetEvent?.name;
    case 'fundraiser':
      return activity.targetFundraiser?.name;
    case 'team':
      return activity.targetTeam?.name;
    case 'collection':
      return activity.targetCollection?.name;
    case 'donation':
      return undefined; // Uses ID instead
    case 'donationSubscription':
      return undefined; // Uses ID instead
    case 'sale':
      return undefined; // Uses ID instead
    case 'saleDetail':
      return activity.targetSaleDetail?.ticketName || activity.targetSaleDetail?.merchandiseName;
    case 'ticket':
      return activity.targetTicket?.name;
    case 'merchandise':
      return activity.targetMerchandise?.name;
    case 'series':
      return activity.targetSeries?.name;
    case 'franchise':
      return activity.targetFranchise?.name;
    case 'campaignAmbassadorProgram':
      return activity.targetCampaignAmbassadorProgram?.eventName;
    case 'eventAmbassadorProgram':
      return activity.targetEventAmbassadorProgram?.eventName;
    case 'eventAmbassador':
      return formatAccountName(activity.targetEventAmbassador as AccountActivityAccountModel | null);
    case 'emailInstance':
      return activity.targetEmailInstance?.templateName;
    case 'saleCoupon':
      return undefined;
    case 'emailLog':
      return undefined;
    case 'integration':
      return undefined;
    default:
      return undefined;
  }
};

/**
 * Get target entity ID based on the target type
 */
const getTargetId = (activity: AccountActivityElementModel): number | undefined => {
  const { targetEntity } = activity;

  if (!targetEntity) return undefined;

  switch (targetEntity) {
    case 'account':
      return activity.targetAccountId ?? undefined;
    case 'organization':
      return activity.targetOrganizationId ?? undefined;
    case 'accessKey':
      return activity.targetAccessKeyId ?? undefined;
    case 'campaign':
      return activity.targetCampaignId ?? undefined;
    case 'event':
      return activity.targetEventId ?? undefined;
    case 'fundraiser':
      return activity.targetFundraiserId ?? undefined;
    case 'team':
      return activity.targetTeamId ?? undefined;
    case 'collection':
      return activity.targetCollectionId ?? undefined;
    case 'donation':
      return activity.targetDonationId ?? undefined;
    case 'donationSubscription':
      return activity.targetDonationSubscriptionId ?? undefined;
    case 'sale':
      return activity.targetSaleId ?? undefined;
    case 'saleDetail':
      return activity.targetSaleDetailId ?? undefined;
    case 'ticket':
      return activity.targetTicketId ?? undefined;
    case 'merchandise':
      return activity.targetMerchandiseId ?? undefined;
    case 'series':
      return activity.targetSeriesId ?? undefined;
    case 'franchise':
      return activity.targetFranchiseId ?? undefined;
    case 'campaignAmbassadorProgram':
      return activity.targetCampaignAmbassadorProgramId ?? undefined;
    case 'eventAmbassadorProgram':
      return activity.targetEventAmbassadorProgramId ?? undefined;
    case 'eventAmbassador':
      return activity.targetEventAmbassadorId ?? undefined;
    case 'emailInstance':
      return activity.targetEmailInstanceId ?? undefined;
    case 'saleCoupon':
      return undefined; // saleCoupon doesn't have an ID field
    case 'emailLog':
      return undefined; // Export-only entity
    case 'integration':
      return undefined;
    default:
      return undefined;
  }
};

/**
 * Row count suffix for an export activity, empty when no register entry came back.
 *
 * Absent rather than zero for exports recorded before the register linkage existed, and for the
 * API-key exports that stay unregistered until GR-3855, so an empty suffix means "not recorded"
 * rather than "exported nothing".
 */
const formatExportSize = (activity: AccountActivityElementModel): string => {
  const resultCount = activity.targetDataExportEntry?.resultCount;
  if (resultCount === undefined || resultCount === null) {
    return '';
  }

  // Locale pinned rather than left to the runtime default. The surrounding copy is hardcoded en-AU
  // English, so a browser set elsewhere would otherwise render "4.210 records" inside an English
  // sentence, and the assertions on this would depend on whichever locale the test host happened to
  // have.
  const formatted = resultCount.toLocaleString('en-AU');

  return ` (${formatted} ${resultCount === 1 ? 'record' : 'records'})`;
};

/**
 * Format target entity description and link
 */
export const formatTarget = (activity: AccountActivityElementModel): TargetInfo | null => {
  const { targetEntity, activityType } = activity;

  // Login activities don't have a target - don't show anything
  if (activityType === 'login') {
    return null;
  }

  // Request-path overrides
  if (activityType === 'read' && activity.requestPath) {
    // Data export download: "{user} downloaded a data export"
    if (/^\/api\/v\d+\/dataexport\/download\/\d+$/i.test(activity.requestPath)) {
      return { description: 'a data export', link: null };
    }
    // Backoffice configuration view: keep normal target description (campaign/org/event name)
    // The action verb is already overridden by formatAction
  }

  // When no target entity is specified, show generic "a record" text
  if (!targetEntity) {
    return { description: 'a record', link: null };
  }

  // For export activities, show "a {entity} list" instead of the specific entity name. Where the
  // export register entry came back, append how many rows actually left the platform: the entity name
  // says which kind of data, the count is the part a breach assessment has to be able to state.
  //
  // Ahead of the targetEntityConfig lookup because this branch never uses the config, and an entity
  // the config does not know would otherwise fall out as "exported a record". The map is typed total
  // over ActivityEntity, so that only happens when core sends an entity this union has not caught up
  // with, which is routine given the two repos ship separately. 'report' arrived exactly that way.
  if (activityType === 'export') {
    return {
      description: `a ${targetEntity} list${formatExportSize(activity)}`,
      link: null
    };
  }

  const config = targetEntityConfig[targetEntity];
  if (!config) {
    return { description: 'a record', link: null };
  }

  const name = getTargetName(activity);
  const id = getTargetId(activity);
  const description = config.getDescription(name, id);

  // Handle saleDetail which needs saleId from the model
  let link: string | null = null;
  if (id !== undefined) {
    if (targetEntity === 'saleDetail') {
      link = config.getLink(id, { saleId: activity.targetSaleDetail?.saleId });
    } else {
      link = config.getLink(id);
    }
  }

  return { description, link };
};

// ═══════════════════════════════════════════════════════════════════════════
// Outcome Formatting
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get outcome styling configuration
 */
export const getOutcomeConfig = (outcome: AccountActivityOutcome): OutcomeConfig => {
  return outcomeConfig[outcome] || outcomeConfig.success;
};

// ═══════════════════════════════════════════════════════════════════════════
// Metadata Formatting
// ═══════════════════════════════════════════════════════════════════════════

export interface ActivityMetadata {
  ipAddress: string | null;
  userAgent: string | null;
  requestMethod: string | null;
  requestPath: string | null;
  activityType: string;
  activitySubtype: string | null;
  /** Whether to display the subtype in metadata (false when subtype is already reflected in action verb) */
  showSubtype: boolean;
  adminContextType: string | null;
  adminRole: string | null;
  accessKeyDescription: string | null;
  mfaUsed: boolean;
}

/**
 * Extract metadata for secondary display
 */
export const extractMetadata = (activity: AccountActivityElementModel): ActivityMetadata => {
  const { activitySubtype } = activity;
  
  // Don't show subtype in metadata if it's already reflected in the action verb
  const showSubtype = activitySubtype 
    ? !activitySubtypeVerbs[activitySubtype] 
    : false;

  return {
    ipAddress: activity.ipAddress || null,
    userAgent: activity.userAgent || null,
    requestMethod: activity.requestMethod || null,
    requestPath: activity.requestPath || null,
    activityType: activity.activityType,
    activitySubtype: activitySubtype || null,
    showSubtype,
    adminContextType: activity.adminContextType || null,
    adminRole: activity.adminRole || null,
    accessKeyDescription: activity.accessKey?.description || null,
    mfaUsed: activity.mfaUsed,
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// Payload Formatting
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Format payload object for tooltip display
 * Returns array of { key, value } pairs
 */
export const formatPayload = (
  payload: Record<string, unknown> | null
): { key: string; value: string }[] => {
  if (!payload) return [];

  return Object.entries(payload).map(([key, value]) => ({
    key,
    value: typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''),
  }));
};

// ═══════════════════════════════════════════════════════════════════════════
// Full Activity Summary
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build a complete activity summary string
 * e.g., "john@example.com updated campaign "City 123""
 */
export const buildActivitySummary = (activity: AccountActivityElementModel): string => {
  const actor = formatActor(activity);
  const action = formatAction(activity);
  const target = formatTarget(activity);

  let summary = `${actor.displayName} ${action}`;

  if (target) {
    summary += ` ${target.description}`;
  }

  return summary;
};
